import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  registerTableCellUnmergeTransform,
  registerTablePlugin,
  registerTableSelectionObserver,
  TableCellNode,
  TableNode,
} from "@lexical/table";
import type { LexicalEditor } from "lexical";
import { type FC, useEffect } from "react";
import { DataTableNode } from "../nodes/data-table-node";

/**
 * Drop-in replacement for `@lexical/react/LexicalTablePlugin` that also
 * registers {@link DataTableNode} with the table-selection observer system.
 *
 * ### Why this exists
 *
 * `DataTableNode` extends `TableNode` but has its own Lexical type
 * (`"data-table"` vs `"table"`).  Lexical's mutation listeners are keyed by
 * type, so `registerTableSelectionObserver` — which only registers a listener
 * for `TableNode` — never sees `DataTableNode` mutations and never creates a
 * table-observer for them.
 *
 * The monolithic `SELECTION_CHANGE_COMMAND` handler introduced in v0.42.0
 * *does* detect data-tables (via `$isTableNode` / `instanceof`), then tries
 * to look the observer up and throws `"tableObserver not found"` because none
 * was ever created.  This fires on every selection change while the cursor is
 * inside a data-table cell, flooding the console and making the page unusable.
 *
 * ### How the fix works
 *
 * `registerTableSelectionObserver` internally calls
 * `editor.registerMutationListener(TableNode, callback)`.  We temporarily
 * wrap that method so that the *same* `callback` is also registered for
 * `DataTableNode`.  The wrapper is applied and removed inside a single
 * `useEffect` — it only lives long enough for the one synchronous
 * `registerTableSelectionObserver` call — so nothing else in the editor is
 * affected.  The combined unregister function ensures both listeners share
 * the same lifecycle.
 *
 * ### Removal
 *
 * Delete this file and switch back to `@lexical/react/LexicalTablePlugin`
 * if upstream Lexical adds support for additional table node types in
 * `registerTableSelectionObserver`.
 */

/**
 * Calls {@link registerTableSelectionObserver} with a scoped patch that also
 * registers `DataTableNode` alongside `TableNode` in the same observer
 * lifecycle.
 *
 * Extracted as a standalone function so that the temporary mutation of
 * `editor.registerMutationListener` does not trigger the
 * `react-hooks/immutability` lint rule (which forbids modifying values
 * returned from hooks).
 */
function registerTableSelectionObserverWithDataTable(
  editor: LexicalEditor,
  hasTabHandler: boolean,
): () => void {
  if (!editor.hasNodes([DataTableNode])) {
    return registerTableSelectionObserver(editor, hasTabHandler);
  }

  const original: typeof editor.registerMutationListener =
    editor.registerMutationListener.bind(editor);

  let patchApplied = false;

  editor.registerMutationListener = function patchedRegisterMutationListener(
    klass,
    listener,
    options,
  ) {
    const unregister = original(klass, listener, options);

    const isTableNodeRegistration =
      !patchApplied &&
      (klass === TableNode ||
        (typeof (klass as typeof TableNode).getType === "function" &&
          (klass as typeof TableNode).getType() === "table"));

    if (isTableNodeRegistration) {
      patchApplied = true;

      // Register the *same* callback for DataTableNode so that the internal
      // `tableObservers` map is populated for both node types.
      const dataTableUnregister = original(DataTableNode, listener, options);

      // Return a combined teardown so `mergeRegister` inside
      // `registerTableSelectionObserver` cleans up both listeners together.
      return () => {
        unregister();
        dataTableUnregister();
      };
    }

    return unregister;
  };

  const cleanup = registerTableSelectionObserver(editor, hasTabHandler);

  // Restore the original method immediately — the patch only needed to live
  // for the duration of the synchronous `registerTableSelectionObserver`
  // call above.
  editor.registerMutationListener = original;

  return cleanup;
}

interface TablePluginProps {
  hasCellMerge?: boolean;
  hasCellBackgroundColor?: boolean;
  hasTabHandler?: boolean;
}

export const TablePlugin: FC<TablePluginProps> = ({
  hasCellMerge = true,
  hasCellBackgroundColor = true,
  hasTabHandler = true,
}) => {
  const [editor] = useLexicalComposerContext();

  // Register core table commands & transforms (INSERT_TABLE_COMMAND, structural
  // transforms for TableNode / TableRowNode / TableCellNode, etc.)
  useEffect(() => registerTablePlugin(editor), [editor]);

  // Register the table-selection observer for **both** TableNode and
  // DataTableNode.
  useEffect(
    () => registerTableSelectionObserverWithDataTable(editor, hasTabHandler),
    [editor, hasTabHandler],
  );

  // When cell-merge is disabled, force all cells to colSpan/rowSpan 1.
  useEffect(() => {
    if (!hasCellMerge) {
      return registerTableCellUnmergeTransform(editor);
    }
  }, [editor, hasCellMerge]);

  // When cell background colour is disabled, strip it on every mutation.
  useEffect(() => {
    if (!hasCellBackgroundColor) {
      return editor.registerNodeTransform(TableCellNode, (node) => {
        if (node.getBackgroundColor() !== null) {
          node.setBackgroundColor(null);
        }
      });
    }
  }, [editor, hasCellBackgroundColor]);

  return null;
};
